import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PROFILE } from "../data/portfolioData";
import { MenuIcon, CloseIcon } from "./ui/Icons";
import Socials from "./ui/Socials";
import ThemeToggle from "./ui/ThemeToggle";

export const NAV_LINKS = [
  { name: "professional", path: "/" },
  { name: "personal", path: "/personal" },
  { name: "contact", path: "/contact" },
];

const NavLink = ({ link, active, navigate, className = "" }) => (
  <a
    href={`#${link.path}`}
    onClick={(e) => {
      e.preventDefault();
      navigate(link.path);
    }}
    className={`${active ? "text-accent border-b-2 border-accent" : ""} capitalize font-medium hover:text-accent transition-all ${className}`}
  >
    {link.name}
  </a>
);

const MobileNav = ({ route, navigate }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex justify-center items-center text-accent"
        aria-label="Open menu"
      >
        <MenuIcon size={32} />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-ink/40 z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="fixed right-0 top-0 h-full w-[80vw] max-w-sm bg-primary border-l border-ink/10 z-[70] flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="self-end m-8 text-accent"
                aria-label="Close menu"
              >
                <CloseIcon size={28} />
              </button>
              <div className="mt-16 mb-24 text-center text-2xl">
                {PROFILE.firstName}
                <span className="text-accent">.</span>
              </div>
              <nav className="flex flex-col justify-center items-center gap-8">
                {NAV_LINKS.map((link) => (
                  <NavLink key={link.path} link={link} active={route === link.path} navigate={go} className="text-xl" />
                ))}
              </nav>
              <div className="mt-auto mb-12 flex justify-center">
                <Socials />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Fixed to the top; gains a frosted beige backdrop and tightens once the page scrolls.
const Header = ({ route, navigate }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 text-ink transition-all duration-300 ${
        scrolled
          ? "py-4 bg-primary/80 backdrop-blur-md border-b border-ink/10 shadow-[0_6px_24px_rgb(33_29_27/0.06)]"
          : "py-8 xl:py-12 border-b border-transparent"
      }`}
    >
      <div className="container mx-auto flex justify-between items-center">
        <a
          href="#/"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          <h1 className={`font-semibold transition-all duration-300 ${scrolled ? "text-3xl" : "text-4xl"}`}>
            {PROFILE.firstName} <span className="text-accent">.</span>
          </h1>
        </a>
        <div className="hidden xl:flex items-center gap-8">
          <nav className="flex gap-8">
            {NAV_LINKS.filter((link) => link.path !== "/contact").map((link) => (
              <NavLink key={link.path} link={link} active={route === link.path} navigate={navigate} />
            ))}
          </nav>
          <a
            href="#/contact"
            onClick={(e) => {
              e.preventDefault();
              navigate("/contact");
            }}
            className={`inline-flex items-center justify-center rounded-full font-semibold h-[44px] px-6 transition-colors ${
              route === "/contact" ? "bg-accent text-primary" : "bg-ink text-primary hover:bg-accent"
            }`}
          >
            Contact
          </a>
          <ThemeToggle />
        </div>
        <div className="xl:hidden flex items-center gap-4">
          <ThemeToggle />
          <MobileNav route={route} navigate={navigate} />
        </div>
      </div>
    </header>
  );
};

export default Header;
